import { t } from "@lingui/macro";
import { Download, Pin, X } from "lucide-react";
import { useState } from "react";
import { ExternalShareLinkDownload } from "../../models/ExternalShareLink";
import { RoomItem, UIRoomItem } from "../../models/RoomItem";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import { ViewMode } from "../rooms/right-menu/RoomFilesModal";
import { ItemPopover } from "./ItemPopover";
import { FileIcon, FileIconThumb, ImageViewerModal, FileIconList } from "./MessageFileItem";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { ScrollArea } from "../../components/ui/scroll-area";

export const ItemFilePopover: React.FC<{ item: RoomItem, canEdit: boolean, viewMode: ViewMode }> = ({ item, canEdit, viewMode }) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  return (
    <>
      <ItemPopover item={item} canEdit={canEdit}>
        <div className="w-full cursor-pointer outline-none">
          {viewMode === 'list' && (
            <div className="flex items-center gap-3 py-2 px-3 hover:bg-slate-50 rounded-lg group text-sm">
              <div className="flex-1 flex items-center gap-3 min-w-0">
                <div className="relative shrink-0">
                  {item.pinned && <Pin className="absolute -top-1 -left-1 h-3 w-3 text-orange-500 fill-current rotate-[-45deg] z-10" />}
                  <FileIcon filename={item.url?.split('?')[0] ?? ''} />
                </div>
                <a
                  onClick={(e) => { e.stopPropagation(); window.open(item.url, '_blank') }}
                  title={item.url}
                  className="truncate hover:text-blue-600 hover:underline cursor-pointer flex-1"
                >
                  {item.title ?? item.url}
                </a>
              </div>
              <div className="hidden lg:block w-32 text-gray-500 text-xs shrink-0 text-right">
                {UtilApp.dateStrToDay(item.created, My.user.lang)}
              </div>
              <div className="hidden lg:block w-32 text-gray-700 font-medium shrink-0 text-right truncate">
                {item.user.name}
              </div>
            </div>
          )}

          {viewMode === 'grid' && (
            <div className="flex flex-col gap-2 p-2 hover:bg-slate-50 rounded-lg">
              <div className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center border border-slate-200">
                {Util.isImageUrl(item.url)
                  ? <img
                    src={item.url}
                    title={item.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    onClick={(e) => { e.stopPropagation(); setIsViewerOpen(true); }}
                    alt={item.title}
                  />
                  : <FileIcon filename={item.title ?? ''} className="w-12 h-12" />
                }
              </div>
              <p className="text-xs font-medium text-center truncate px-1 text-gray-700" title={item.title}>
                {item.title}
              </p>
            </div>
          )}
        </div>
      </ItemPopover>

      {isViewerOpen && <ImageViewerModal images={[item.url!]} currentImage={0} onClose={() => setIsViewerOpen(false)} />}
    </>
  );
}

function isSelectedItems(items: RoomItem[], item: RoomItem) {
  return items.some(sitem => sitem.id === item.id);
}

export default ItemFilePopover;

export const FileShareItem: React.FC<{ item: RoomItem, selectedItems: RoomItem[], downloads: ExternalShareLinkDownload[], checkable: boolean, remove: () => void }> = ({ item, selectedItems, downloads, checkable, remove }) => {
  const [downloadAlertOpen, setDownloadAlertOpen] = useState(false);
  const { toast } = useToast();
  // Force update for checkbox state since selectedItems partial mutation doesn't trigger re-render
  const [_, setForceUpdate] = useState(0);

  if (checkable) {
    return (
      <div className="flex items-center gap-3 py-2 px-3 border-b border-gray-100 last:border-0 hover:bg-slate-50 text-sm">
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <Checkbox
            checked={isSelectedItems(selectedItems, item)}
            onCheckedChange={(checked) => onChange(checked as boolean)}
          />
          <MouseOverPreview imageUrl={item.url} content={itemTitle} />
        </div>
        <div className="hidden lg:block w-32 text-gray-700 font-medium shrink-0">
          {item.user.name}
        </div>
        <div className="hidden lg:block w-12 shrink-0 text-right">
          {downloads.length > 0 &&
            <button onClick={showDownloads} className="flex items-center justify-end gap-1 text-blue-500 hover:text-blue-700">
              <Download className="w-3.5 h-3.5" />
              <span className="text-xs">{downloads.length}</span>
            </button>
          }
        </div>
        <DownloadInfoDialog open={downloadAlertOpen} onOpenChange={setDownloadAlertOpen} downloads={downloads} title={item.title} />
      </div>
    );
  }
  else {
    return (
      <div className="flex items-center gap-3 py-2 px-3 border border-gray-200 rounded-lg hover:bg-slate-50 bg-white">
        <div className="flex-1 min-w-0">
          {itemTitle1()}
        </div>
        {downloads.length > 0 && (
          <Button variant="ghost" size="sm" onClick={showDownloads} className="gap-1 text-gray-500 h-8 px-2">
            <Download className="w-3.5 h-3.5" /> <span>{downloads.length}</span>
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-500" onClick={removeItem}>
          <X className="w-4 h-4" />
        </Button>
        <DownloadInfoDialog open={downloadAlertOpen} onOpenChange={setDownloadAlertOpen} downloads={downloads} title={item.title} />
      </div>
    );
  }

  function itemTitle1() {
    const uitem = new UIRoomItem(item);
    return (
      <div className="flex items-center gap-3">
        <div className="shrink-0"><FileIconList uitem={uitem} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <a href={item.url} target={'_blank'} className="text-sm font-medium hover:underline truncate block text-gray-900" rel="noreferrer">
              {item.title ?? item.url}
            </a>
            {item.pinned && <Pin className="h-3 w-3 text-orange-500 fill-current" />}
          </div>
          <p className="text-xs text-gray-500 truncate">{item.user.name}</p>
        </div>
      </div>
    );
  }

  function itemTitle() {
    return (
      <div className="flex items-center gap-3 min-w-0">
        <FileIconThumb filename={item.title ?? ''} url={item.url ?? ''} />
        <div className="min-w-0 flex-1 relative">
          <a href={item.url} target={'_blank'} className="text-sm font-medium hover:underline truncate block text-gray-900" rel="noreferrer">
            {item.title ?? item.url}
          </a>
          {item.pinned && <Pin className="absolute -top-2 -right-2 h-3 w-3 text-orange-500 fill-current rotate-[-45deg]" />}
        </div>
      </div>
    );
  }

  function onChange(checked: boolean) {
    if (checked) {
      if (!isSelectedItems(selectedItems, item)) {
        selectedItems.push(item);
      }
    } else {
      const idx = selectedItems.findIndex(i => i.id === item.id);
      if (idx !== -1) selectedItems.splice(idx, 1);
    }
    setForceUpdate(n => n + 1);
  }

  function removeItem() {
    if (selectedItems.length <= 1) { // Logic check: originally === 1, assumes cannot remove last? "Cannot delete the last file"
      toast({ description: t`Cannot delete the last file`, variant: "destructive" });
      return;
    }
    const idx = selectedItems.findIndex(i => i.id === item.id);
    if (idx !== -1) {
      selectedItems.splice(idx, 1);
      remove();
    }
  }

  function showDownloads() {
    if (downloads.length > 0)
      setDownloadAlertOpen(true);
  }
}

const DownloadInfoDialog = ({ open, onOpenChange, downloads, title }: { open: boolean, onOpenChange: (o: boolean) => void, downloads: ExternalShareLinkDownload[], title?: string | null }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t`Downloads for ${title}`}</DialogTitle>
      </DialogHeader>
      <ScrollArea className="max-h-[60vh]">
        <div className="space-y-2 p-1">
          {downloads.map((d, i) => {
            const user = d.userId ? My.getUserFromRooms(d.userId)?.name ?? t`UserId: ${d.userId}` : t`Anonymous (ip: ${d.ip})`;
            return (
              <div key={i} className="flex flex-col text-sm border-b pb-2 last:border-0 border-gray-100">
                <span className="font-medium">{user}</span>
                <span className="text-gray-500 text-xs">{UtilApp.dateStrToTimeAndDay(d.created, My.user.lang)}</span>
              </div>
            )
          })}
        </div>
      </ScrollArea>
      <DialogFooter>
        <Button onClick={() => onOpenChange(false)}>{t`OK`}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)

export const MouseOverPreview: React.FC<{ imageUrl: string | undefined, content: () => React.ReactNode }> = ({ imageUrl, content }) => {
  const [showPreview, setShowPreview] = useState(false);

  // Consider disabling preview on touch devices or small screens
  const handleMouseOver = () => {
    if (Util.isImageUrl(imageUrl))
      setShowPreview(true);
  };

  const handleMouseLeave = () => {
    setShowPreview(false);
  };

  return (
    <div className="relative" onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
      {content()}
      {showPreview && imageUrl && (
        <div className="absolute left-[200px] top-1/2 -translate-y-1/2 z-[100] bg-white p-2 rounded shadow-xl border border-gray-200 pointer-events-none">
          <img src={imageUrl} className="max-w-[250px] max-h-[250px] object-contain rounded" alt="preview" />
        </div>
      )}
    </div>
  )

}
